import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import {
  createPost,
  getAuthenticatedProfile,
  saveBlueskyDescription,
  saveSkyspaceAesthetics,
  saveSkyspaceContent,
  saveSkyspaceProfile,
  setFollowing,
  uploadBackgroundImage,
} from '../../data/authenticated';
import { publishProfileComment } from '../../data/profile-comments';
import { queryKeys } from '../../data/queries';
import { recordSkyspaceActivity, trackSiteEvent } from '../../data/site-services';
import {
  normalizeClassicTheme,
  parseInterests,
  serializeInterests,
  type ClassicTheme,
} from '../../domain/classic-profile';
import {
  customPanelSlots,
  normalizeProfileLayout,
  type ProfileLayout,
} from '../../domain/profile-layout';
import type {
  AestheticsRecord,
  ActorProfile,
  ContentRecord,
  CustomPanel,
  SkyspaceProfileContent,
} from '../../domain/types';
import { EditGlyph } from '../../ui/EditGlyph';
import { hexToHsv, hsvToHex } from '../../lib/colour';
import { shadertoyId } from '../../lib/shadertoy';
import {
  customShaderSource,
  MAX_SHADER_SOURCE_LENGTH,
  shaderPreset,
  shaderPresets,
  shaderSourceProblem,
} from '../../lib/shaders';
import { RichText } from './ProfileSections';

const LayoutEditor = lazy(() => import('./LayoutEditor').then(module => ({
  default: module.LayoutEditor,
})));

export function FollowButton({ profile }: { profile: ActorProfile }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const [followingOverride, setFollowingOverride] = useState<string | null>();
  const authenticatedProfile = useQuery({
    queryKey: ['authenticated-profile', auth.did, profile.did],
    queryFn: () => getAuthenticatedProfile(auth.rpc!, profile.did),
    enabled: Boolean(auth.rpc && auth.did && auth.did !== profile.did),
  });
  useEffect(() => setFollowingOverride(undefined), [profile.did]);
  if (!auth.rpc || !auth.did) return null;
  if (auth.did === profile.did) {
    return (
      <button
        type="button"
        className="contact-link modern-contact-button modern-self-contact"
        disabled
        aria-disabled="true"
      >
        <img src="../add-friend-icon.png" alt="" className="contact-icon add-friend-icon" />
        Add to Friends
      </button>
    );
  }
  const following = followingOverride === undefined
    ? authenticatedProfile.data?.viewer?.following
    : followingOverride || undefined;
  return (
    <>
      <button
        type="button"
        className="contact-link modern-contact-button"
        disabled={pending || authenticatedProfile.isPending}
        onClick={() => {
          setPending(true);
          setError(undefined);
          void setFollowing(auth.rpc!, auth.did!, profile.did, following)
            .then(result => {
              setFollowingOverride(result || null);
              return queryClient.invalidateQueries({
                queryKey: ['authenticated-profile', auth.did, profile.did],
              });
            })
            .catch(reason => setError(reason instanceof Error ? reason.message : 'Could not update this friendship.'))
            .finally(() => setPending(false));
        }}
      >
        <img src="../add-friend-icon.png" alt="" className="contact-icon add-friend-icon" />
        {following ? 'Remove Friend' : 'Add to Friends'}
      </button>
      {error && <span className="sr-only" role="alert">{error}</span>}
    </>
  );
}

export function EditMarker({
  label,
  onClick,
}: {
  label: string;
  onClick(): void;
}) {
  return (
    <button type="button" className="modern-edit-marker" onClick={onClick}>
      <EditGlyph />
      <span>{label}</span>
    </button>
  );
}

export function LeaveComment({
  profile,
  onSaved,
}: {
  profile: ActorProfile;
  onSaved(): void;
}) {
  const auth = useAuth();
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  if (!auth.rpc || !auth.did) return null;
  return (
    <form className="modern-profile-composer modern-comment-composer" onSubmit={event => {
      event.preventDefault();
      setPending(true);
      setError(undefined);
      void publishProfileComment(auth.rpc!, auth.did!, profile.did, text)
        .then(() => {
          recordSkyspaceActivity(auth.did, auth.handle, 'comment');
          setText('');
          onSaved();
        })
        .catch(reason => setError(reason instanceof Error ? reason.message : 'Could not post the comment.'))
        .finally(() => setPending(false));
    }}>
      <img
        className="modern-composer-avatar"
        src={auth.avatar || '../placeholder.png'}
        alt={`@${auth.handle || 'you'}`}
        width="48"
        height="48"
      />
      <div className="modern-composer-body">
        <label className="sr-only" htmlFor="modern-profile-comment">Leave a comment</label>
        <textarea
          id="modern-profile-comment"
          value={text}
          onChange={event => setText(event.target.value)}
          maxLength={240}
          placeholder={`Write a comment for ${profile.displayName || `@${profile.handle}`}…`}
          required
        />
        <div className="modern-composer-footer">
          <span>{text.length}/240</span>
          <button type="submit" disabled={pending || !text.trim()}>
            {pending ? 'Posting…' : 'Post comment'}
          </button>
        </div>
        {error && <p role="alert">{error}</p>}
      </div>
    </form>
  );
}

export function NewPostControl({
  profile,
  onSaved,
}: {
  profile: ActorProfile;
  onSaved(): void;
}) {
  const auth = useAuth();
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  if (!auth.rpc || !auth.did || auth.did !== profile.did) return null;
  return (
    <form className="modern-profile-composer modern-new-post-editor" onSubmit={event => {
      event.preventDefault();
      setPending(true);
      setError(undefined);
      void createPost(auth.rpc!, auth.did!, text)
        .then(() => {
          recordSkyspaceActivity(auth.did, auth.handle || profile.handle, 'post');
          setText('');
          onSaved();
        })
        .catch(reason => setError(reason instanceof Error ? reason.message : 'Could not publish the post.'))
        .finally(() => setPending(false));
    }}>
      <img
        className="modern-composer-avatar"
        src={auth.avatar || profile.avatar || '../placeholder.png'}
        alt=""
        width="48"
        height="48"
      />
      <div className="modern-composer-body">
        <label className="sr-only" htmlFor="modern-new-post">Write a new post</label>
        <textarea
          id="modern-new-post"
          value={text}
          onChange={event => setText(event.target.value)}
          maxLength={300}
          placeholder="Write a new post…"
          required
        />
        <div className="modern-composer-footer">
          <span>{text.length}/300</span>
          <button type="submit" disabled={pending || !text.trim()}>
            {pending ? 'Posting…' : 'Post'}
          </button>
        </div>
        {error && <p role="alert">{error}</p>}
      </div>
    </form>
  );
}

function ContextEditorActions({
  pending,
  saveLabel,
  cancel,
}: {
  pending: boolean;
  saveLabel: string;
  cancel(): void;
}) {
  return (
    <div className="modern-context-editor-actions">
      <button type="submit" disabled={pending}>{pending ? 'Saving…' : saveLabel}</button>
      <button type="button" disabled={pending} onClick={cancel}>Cancel</button>
    </div>
  );
}

export function MusicEditor({
  profile,
  data,
  value,
  renderPreview,
  defaultOpen = false,
}: {
  profile: ActorProfile;
  data?: SkyspaceProfileContent;
  value: string;
  renderPreview(value: string): React.ReactNode;
  defaultOpen?: boolean;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const savedValue = useRef(value);
  useEffect(() => {
    savedValue.current = value;
    if (!open) setDraft(value);
  }, [open, value]);
  if (!auth.rpc || auth.did !== profile.did) return null;
  if (!open) {
    return (
      <>
        {value && renderPreview(value)}
        <EditMarker
          label={value ? 'Edit Music' : 'Add Music'}
          onClick={() => {
            setDraft(value);
            setError(undefined);
            setOpen(true);
          }}
        />
      </>
    );
  }
  const save = (nextValue: string) => {
    const normalized = nextValue.trim();
    if (normalized === savedValue.current.trim()) {
      setOpen(false);
      return;
    }
    setPending(true);
    setError(undefined);
    void saveSkyspaceContent(
      auth.rpc!,
      auth.did!,
      { ...(data?.content?.value || {}), music: normalized },
      data?.content?.uri,
    ).then(async () => {
      savedValue.current = normalized;
      await queryClient.invalidateQueries({ queryKey: queryKeys.skyspaceContent(profile.did) });
      recordSkyspaceActivity(auth.did, auth.handle || profile.handle, 'music');
      setOpen(false);
    }).catch(reason => {
      setError(reason instanceof Error ? reason.message : 'Could not save profile music.');
    }).finally(() => setPending(false));
  };
  return (
    <div className="modern-direct-music-editor">
      {draft.trim() && renderPreview(draft.trim())}
      <label className="modern-direct-music-url">
        <EditGlyph />
        <span className="sr-only">Profile music URL</span>
        <input
          type="url"
          value={draft}
          placeholder="Your music URL"
          disabled={pending}
          onChange={event => setDraft(event.target.value)}
          onBlur={event => save(event.currentTarget.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            } else if (event.key === 'Escape') {
              setDraft(savedValue.current);
              setOpen(false);
            }
          }}
          autoFocus
        />
        {pending && <span className="modern-direct-save-state">Saving…</span>}
      </label>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

export function InterestsEditor({
  profile,
  data,
  value,
  children,
  defaultOpen = false,
}: {
  profile: ActorProfile;
  data?: SkyspaceProfileContent;
  value: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const parsedValue = useMemo(() => parseInterests([], value), [value]);
  const [groups, setGroups] = useState(
    parsedValue.length > 0 ? parsedValue : [{ category: '', terms: [] }],
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!open) {
      setGroups(parsedValue.length > 0 ? parsedValue : [{ category: '', terms: [] }]);
    }
  }, [open, parsedValue]);
  if (!auth.rpc || auth.did !== profile.did) return <>{children}</>;
  if (!open) {
    return (
      <>
        {children}
        <EditMarker
          label={value ? 'Edit Interests' : 'Add Interests'}
          onClick={() => {
            setGroups(parsedValue.length > 0 ? parsedValue : [{ category: '', terms: [] }]);
            setError(undefined);
            setOpen(true);
          }}
        />
      </>
    );
  }
  return (
    <form className="modern-context-editor modern-interests-context-editor" onSubmit={event => {
      event.preventDefault();
      const serialized = serializeInterests(groups);
      setPending(true);
      setError(undefined);
      void saveSkyspaceContent(
        auth.rpc!,
        auth.did!,
        { ...(data?.content?.value || {}), interests: serialized },
        data?.content?.uri,
      ).then(async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.skyspaceContent(profile.did) });
        recordSkyspaceActivity(auth.did, auth.handle || profile.handle, 'interests');
        trackSiteEvent('interests_saved', { profile: profile.handle });
        setOpen(false);
      }).catch(reason => {
        setError(reason instanceof Error ? reason.message : 'Could not save your interests.');
      }).finally(() => setPending(false));
    }}>
      <div className="modern-interest-groups">
        {groups.map((group, index) => (
          <div className="modern-interest-group" key={`interest-${index}`}>
            <input
              value={group.category}
              placeholder="Category"
              aria-label={`Interest category ${index + 1}`}
              maxLength={80}
              onChange={event => setGroups(current => current.map((item, itemIndex) => (
                itemIndex === index ? { ...item, category: event.target.value } : item
              )))}
              autoFocus={index === 0}
            />
            <input
              value={group.terms.join(', ')}
              placeholder="thing1, thing2"
              aria-label={`Things in ${group.category || `category ${index + 1}`}`}
              maxLength={500}
              onChange={event => setGroups(current => current.map((item, itemIndex) => (
                itemIndex === index
                  ? {
                    ...item,
                    terms: event.target.value.split(',').map(term => term.trimStart()),
                  }
                  : item
              )))}
            />
            <button
              type="button"
              className="modern-interest-remove"
              aria-label={`Remove ${group.category || `category ${index + 1}`}`}
              disabled={groups.length === 1}
              onClick={() => setGroups(current => current.filter((_, itemIndex) => itemIndex !== index))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="modern-interest-add"
        onClick={() => setGroups(current => [...current, { category: '', terms: [] }])}
      >
        + Add another category
      </button>
      <ContextEditorActions pending={pending} saveLabel="Save Interests" cancel={() => setOpen(false)} />
      {error && <p role="alert">{error}</p>}
    </form>
  );
}

export function QuoteEditor({
  profile,
  data,
  quote,
  location,
}: {
  profile: ActorProfile;
  data?: SkyspaceProfileContent;
  quote: string;
  location: string;
  defaultOpen?: boolean;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [draftQuote, setDraftQuote] = useState(quote);
  const [draftLocation, setDraftLocation] = useState(location);
  const [editingQuote, setEditingQuote] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const quoteInput = useRef<HTMLTextAreaElement>(null);
  const savedValues = useRef({ quote, location });
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  useEffect(() => {
    const previous = savedValues.current;
    setDraftQuote(current => current === previous.quote ? quote : current);
    setDraftLocation(current => current === previous.location ? location : current);
    savedValues.current = { quote, location };
  }, [location, quote]);
  useEffect(() => {
    if (!editingQuote || !quoteInput.current) return;
    quoteInput.current.focus();
    quoteInput.current.style.height = 'auto';
    quoteInput.current.style.height = `${quoteInput.current.scrollHeight}px`;
  }, [draftQuote, editingQuote]);
  if (!auth.rpc || auth.did !== profile.did) return null;
  const save = (nextQuote: string, nextLocation: string) => {
    const normalized = {
      quote: nextQuote.trim(),
      location: nextLocation.trim(),
    };
    if (
      normalized.quote === savedValues.current.quote.trim()
      && normalized.location === savedValues.current.location.trim()
    ) return;
    setPending(true);
    setError(undefined);
    const operation = saveQueue.current.catch(() => undefined).then(async () => {
      await saveSkyspaceAesthetics(
        auth.rpc!,
        auth.did!,
        {
          ...(data?.aesthetics?.value || {}),
          bio: normalized,
        },
        data?.aesthetics?.uri,
      );
      savedValues.current = normalized;
      await queryClient.invalidateQueries({ queryKey: queryKeys.skyspaceContent(profile.did) });
      recordSkyspaceActivity(auth.did, auth.handle || profile.handle, 'profile');
    });
    saveQueue.current = operation;
    void operation.catch(reason => {
      setError(reason instanceof Error ? reason.message : 'Could not save your profile details.');
    }).finally(() => setPending(false));
  };
  return (
    <div className={`modern-direct-profile-fields${pending ? ' is-saving' : ''}`}>
      <div
        className="modern-direct-profile-field modern-direct-quote-field"
        onClick={() => setEditingQuote(true)}
      >
        <EditGlyph />
        {editingQuote ? (
          <textarea
            ref={quoteInput}
            value={draftQuote}
            rows={1}
            maxLength={300}
            placeholder="Your quote"
            aria-label="Your quote"
            onChange={event => setDraftQuote(event.target.value)}
            onBlur={event => {
              setEditingQuote(false);
              save(event.currentTarget.value, draftLocation);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
          />
        ) : (
          <button
            type="button"
            className={`modern-direct-quote-display${quoteFitClass(draftQuote)}`}
          >
            {draftQuote ? `"${draftQuote}"` : 'Your quote'}
          </button>
        )}
      </div>
      <label className="modern-direct-profile-field">
        <EditGlyph />
        <input
          value={draftLocation}
          maxLength={150}
          placeholder="Your location"
          aria-label="Your location"
          onChange={event => setDraftLocation(event.target.value)}
          onBlur={event => save(draftQuote, event.currentTarget.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
        />
      </label>
      {pending && <span className="modern-direct-save-state">Saving…</span>}
      {error && <p role="alert">{error}</p>}
      </div>
  );
}

export function quoteFitClass(value: string): string {
  if (value.length > 150) return ' modern-quote-very-long';
  if (value.length > 75) return ' modern-quote-long';
  return '';
}

export function DirectBlurbEditor({
  profile,
  data,
  section,
  title,
  value,
  placeholder,
  note,
}: {
  profile: ActorProfile;
  data?: SkyspaceProfileContent;
  section: 'about' | 'wiltm';
  title: string;
  value: string;
  placeholder: string;
  note?: string;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const savedValue = useRef(value);
  useEffect(() => {
    const previous = savedValue.current;
    setDraft(current => current === previous ? value : current);
    savedValue.current = value;
  }, [value]);
  if (!auth.rpc || auth.did !== profile.did) return null;
  const save = (nextValue: string) => {
    const normalized = nextValue.trim();
    if (normalized === savedValue.current.trim()) return;
    setPending(true);
    setError(undefined);
    const operation = section === 'about'
      ? saveBlueskyDescription(auth.rpc!, auth.did!, normalized)
      : saveSkyspaceContent(
        auth.rpc!,
        auth.did!,
        { ...(data?.content?.value || {}), wiltm: normalized },
        data?.content?.uri,
      );
    void operation.then(async () => {
      savedValue.current = normalized;
      await queryClient.invalidateQueries({
        queryKey: section === 'about'
          ? queryKeys.profile(profile.handle)
          : queryKeys.skyspaceContent(profile.did),
      });
      recordSkyspaceActivity(auth.did, auth.handle || profile.handle, 'profile');
    }).catch(reason => {
      setError(reason instanceof Error ? reason.message : `Could not save ${title}.`);
    }).finally(() => setPending(false));
  };
  return (
    <div className={`modern-direct-blurb-editor${pending ? ' is-saving' : ''}`}>
      <div className="modern-direct-blurb-title">
        <h2>{title}</h2>
        <button
          type="button"
          className="modern-direct-blurb-edit"
          aria-label={`Edit ${title}`}
          onClick={() => setEditing(true)}
        >
          <EditGlyph />
        </button>
      </div>
      {editing ? (
        <textarea
          value={draft}
          rows={section === 'about' ? 3 : 2}
          placeholder={placeholder}
          aria-label={title}
          maxLength={section === 'about' ? 256 : 3000}
          autoFocus
          onChange={event => setDraft(event.target.value)}
          onBlur={event => {
            const nextValue = event.currentTarget.value.trim();
            setDraft(nextValue);
            setEditing(false);
            save(nextValue);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
        />
      ) : (
        <p
          id={section === 'about' ? 'about-text' : 'who-to-meet-text'}
          className={`modern-direct-blurb-display modern-rich-text${draft ? '' : ' is-placeholder'}`}
          onClick={event => {
            if (!(event.target as Element).closest('a')) setEditing(true);
          }}
        >
          {draft ? <RichText text={draft} autoLink /> : placeholder}
        </p>
      )}
      {note && <p className="modern-direct-blurb-note">{note}</p>}
      {pending && <span className="modern-direct-save-state">Saving…</span>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

interface EditorFields {
  description: string;
  wiltm: string;
  music: string;
  top8: string;
  interests: string;
  quote: string;
  location: string;
  backgroundUrl: string;
  backgroundColor: string;
  backgroundPosition: string;
  backgroundSource: 'upload' | 'url' | 'colour' | 'shader';
  backgroundColorMode: 'solid' | 'pattern' | 'gradient';
  backgroundColorCycleSpeed: string;
  backgroundPattern: 'stripes' | 'checks';
  backgroundColors: string[];
  backgroundGradientAngle: string;
  shaderPreset: string;
  shaderCode: string;
  shaderAttribution: string;
  shaderAttributionUrl: string;
  font: string;
  bodyColor: string;
  titleColor: string;
  linkColor: string;
  panelColor: string;
  panelTransparency: string;
  frameColor: string;
  frameThickness: string;
  frameRadius: string;
  frameStyle: string;
  separatorColor: string;
  cursorImage: string;
  cursorUrl: string;
  cursorTrail: string;
  cursorTrailParameters: string;
}

function fieldString(value: unknown, fallback = ''): string {
  return value === undefined || value === null ? fallback : String(value);
}

function trailSupportsMods(effect: string): boolean {
  return effect !== 'none' && effect !== 'bubbles' && effect !== 'snow';
}

function editorFields(profile: ActorProfile, data?: SkyspaceProfileContent): EditorFields {
  const content = data?.content?.value;
  const aesthetics = data?.aesthetics?.value;
  const backgroundColor = fieldString(aesthetics?.bg?.color, '#ffffff');
  const backgroundColors = aesthetics?.bg?.colors?.length
    ? aesthetics.bg.colors.slice(0, 6)
    : [backgroundColor, '#669acc'];
  const storedSource = aesthetics?.bg?.source;
  const backgroundColorCycle = aesthetics?.bg?.colorCycle === true
    || (
      aesthetics?.bg?.colorCycle === undefined
      && aesthetics?.bg?.colorMode === 'cycle'
    );
  const backgroundSource = storedSource === 'upload'
    || storedSource === 'url'
    || storedSource === 'colour'
    || storedSource === 'shader'
    ? storedSource
    : aesthetics?.bg?.image
      ? 'upload'
      : aesthetics?.bg?.url
        ? 'url'
        : 'colour';
  return {
    description: fieldString(profile.description),
    wiltm: fieldString(content?.wiltm),
    music: fieldString(content?.music),
    top8: fieldString(content?.top8),
    interests: fieldString(content?.interests),
    quote: fieldString(aesthetics?.bio?.quote),
    location: fieldString(aesthetics?.bio?.location),
    backgroundUrl: fieldString(aesthetics?.bg?.url),
    backgroundColor,
    backgroundPosition: fieldString(aesthetics?.bg?.position, 'default'),
    backgroundSource,
    backgroundColorMode: aesthetics?.bg?.colorMode === 'pattern'
      || aesthetics?.bg?.colorMode === 'gradient'
      ? aesthetics.bg.colorMode
      : 'solid',
    backgroundColorCycleSpeed: backgroundColorCycle
      ? fieldString(aesthetics?.bg?.colorCycleSpeed, '60')
      : '0',
    backgroundPattern: aesthetics?.bg?.pattern === 'checks' ? 'checks' : 'stripes',
    backgroundColors,
    backgroundGradientAngle: fieldString(aesthetics?.bg?.gradientAngle, '135'),
    shaderPreset: shaderPreset(aesthetics?.bg?.shader?.preset)
      ? fieldString(aesthetics?.bg?.shader?.preset)
      : aesthetics?.bg?.shader?.preset === 'custom'
        ? 'custom'
        : 'pearlescent',
    shaderCode: fieldString(aesthetics?.bg?.shader?.code),
    shaderAttribution: fieldString(aesthetics?.bg?.shader?.attribution),
    shaderAttributionUrl: fieldString(aesthetics?.bg?.shader?.attributionUrl),
    font: fieldString(aesthetics?.text?.font, 'Arial'),
    bodyColor: fieldString(aesthetics?.text?.bodyColor, '#000000'),
    titleColor: fieldString(aesthetics?.text?.titleColor, '#ff5800'),
    linkColor: fieldString(aesthetics?.text?.linkColor, '#002b99'),
    panelColor: fieldString(aesthetics?.panel?.panelColor, '#ffffff'),
    panelTransparency: fieldString(aesthetics?.panel?.panelTransparency, '0.8'),
    frameColor: fieldString(aesthetics?.panel?.frameColor, '#ffffff'),
    frameThickness: fieldString(aesthetics?.panel?.frameThickness, '0'),
    frameRadius: fieldString(aesthetics?.panel?.frameRadius, '4'),
    frameStyle: fieldString(aesthetics?.panel?.frameStyle, 'solid'),
    separatorColor: fieldString(aesthetics?.panel?.seperatorColor, '#ffcb99'),
    cursorImage: fieldString(aesthetics?.effects?.cursorImage, 'auto'),
    cursorUrl: fieldString(aesthetics?.effects?.cursorURL),
    cursorTrail: fieldString(aesthetics?.effects?.cursorTrail, 'none'),
    cursorTrailParameters: fieldString(aesthetics?.effects?.cursorTrailParameters),
  };
}

function asRecords(fields: EditorFields): {
  content: ContentRecord;
  aesthetics: AestheticsRecord;
} {
  return {
    content: {
      wiltm: fields.wiltm.trim(),
      music: fields.music.trim(),
      top8: fields.top8.trim(),
      interests: fields.interests.trim(),
    },
    aesthetics: {
      bg: {
        url: fields.backgroundUrl.trim(),
        color: fields.backgroundColor.trim(),
        position: fields.backgroundPosition,
        source: fields.backgroundSource,
        colorMode: fields.backgroundColorMode,
        colorCycle: Number(fields.backgroundColorCycleSpeed) > 0,
        colorCycleSpeed: Math.max(
          0,
          Math.min(100, Number(fields.backgroundColorCycleSpeed) || 0),
        ),
        pattern: fields.backgroundPattern,
        colors: fields.backgroundColors,
        gradientAngle: Number(fields.backgroundGradientAngle),
        shader: {
          preset: fields.shaderPreset,
          code: fields.shaderPreset === 'custom' ? fields.shaderCode.trim() : undefined,
          attribution: fields.shaderAttribution.trim(),
          attributionUrl: fields.shaderAttributionUrl.trim(),
        },
      },
      bio: { quote: fields.quote.trim(), location: fields.location.trim() },
      text: {
        font: fields.font.trim(),
        bodyColor: fields.bodyColor,
        titleColor: fields.titleColor,
        linkColor: fields.linkColor,
      },
      panel: {
        panelColor: fields.panelColor,
        panelTransparency: fields.panelTransparency,
        frameColor: fields.frameColor,
        frameThickness: fields.frameThickness,
        frameRadius: fields.frameRadius,
        frameStyle: fields.frameStyle,
        seperatorColor: fields.separatorColor,
      },
      effects: {
        cursorImage: fields.cursorImage,
        cursorURL: fields.cursorUrl.trim(),
        cursorTrail: fields.cursorTrail,
        cursorTrailParameters: trailSupportsMods(fields.cursorTrail)
          ? fields.cursorTrailParameters.trim()
          : '',
      },
    },
  };
}

type StringEditorField = {
  [Key in keyof EditorFields]: EditorFields[Key] extends string ? Key : never;
}[keyof EditorFields];

function SimpleColourPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const safeValue = /^#[0-9a-f]{6}$/i.test(value) ? value : '#ffffff';
  const hsv = hexToHsv(safeValue);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const closeOnResize = () => setOpen(false);
    document.addEventListener('pointerdown', closeOutside);
    window.addEventListener('resize', closeOnResize);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      window.removeEventListener('resize', closeOnResize);
    };
  }, [open]);
  const toggle = () => {
    if (!open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const width = 232;
      const height = 216;
      setPosition({
        top: rect.bottom + height + 8 > window.innerHeight
          ? Math.max(8, rect.top - height - 6)
          : rect.bottom + 6,
        left: Math.max(8, Math.min(window.innerWidth - width - 8, rect.left)),
      });
    }
    setOpen(current => !current);
  };
  const changeSpectrum = (clientX: number, clientY: number) => {
    const spectrum = popupRef.current?.querySelector<HTMLElement>('.modern-colour-spectrum');
    if (!spectrum) return;
    const rect = spectrum.getBoundingClientRect();
    const next = hsvToHex({
      h: hsv.h,
      s: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      v: 1 - Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    });
    setDraft(next);
    onChange(next);
  };
  return (
    <div className="modern-colour-control">
      <span>{label}</span>
      <button
        ref={anchorRef}
        type="button"
        className="modern-colour-button"
        aria-label={`${label}: ${value}`}
        aria-expanded={open}
        onClick={toggle}
      >
        <span style={{ background: safeValue }} />
      </button>
      {open && createPortal(
        <div
          ref={popupRef}
          className="modern-colour-popover"
          role="dialog"
          aria-label={`${label} colour picker`}
          style={position}
        >
          <div
            className="modern-colour-spectrum"
            role="slider"
            tabIndex={0}
            aria-label={`Choose ${label.toLowerCase()} saturation and brightness`}
            aria-valuetext={safeValue}
            style={{ '--modern-colour-hue': `hsl(${hsv.h} 100% 50%)` } as CSSProperties}
            onPointerDown={event => {
              event.currentTarget.setPointerCapture(event.pointerId);
              changeSpectrum(event.clientX, event.clientY);
            }}
            onPointerMove={event => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                changeSpectrum(event.clientX, event.clientY);
              }
            }}
            onKeyDown={event => {
              const step = event.shiftKey ? 0.1 : 0.02;
              const next = { ...hsv };
              if (event.key === 'ArrowLeft') next.s -= step;
              else if (event.key === 'ArrowRight') next.s += step;
              else if (event.key === 'ArrowUp') next.v += step;
              else if (event.key === 'ArrowDown') next.v -= step;
              else return;
              event.preventDefault();
              const nextHex = hsvToHex(next);
              setDraft(nextHex);
              onChange(nextHex);
            }}
          >
            <span
              className="modern-colour-spectrum-thumb"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
            />
          </div>
          <input
            className="modern-colour-hue"
            type="range"
            min="0"
            max="359"
            value={Math.round(hsv.h)}
            aria-label={`Choose ${label.toLowerCase()} hue`}
            onChange={event => {
              const next = hsvToHex({ ...hsv, h: Number(event.target.value) });
              setDraft(next);
              onChange(next);
            }}
          />
          <div className="modern-colour-popover-main">
            <span className="modern-colour-current" style={{ background: safeValue }} />
            <input
              value={draft}
              aria-label={`${label} hex value`}
              spellCheck={false}
              onChange={event => {
                const next = event.target.value;
                setDraft(next);
                if (/^#[0-9a-f]{6}$/i.test(next)) onChange(next);
              }}
              onBlur={() => setDraft(value)}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function Input({
  label,
  field,
  fields,
  setFields,
  type = 'text',
}: {
  label: string;
  field: StringEditorField;
  fields: EditorFields;
  setFields(value: EditorFields): void;
  type?: string;
}) {
  if (type === 'color') {
    return (
      <SimpleColourPicker
        label={label}
        value={fields[field]}
        onChange={value => setFields({ ...fields, [field]: value })}
      />
    );
  }
  return (
    <label>
      <span>{label}</span>
      <input
        data-editor-field={field}
        type={type}
        value={fields[field]}
        onChange={event => setFields({ ...fields, [field]: event.target.value })}
      />
    </label>
  );
}

export type ProfileEditorSection =
  | 'appearance'
  | 'about'
  | 'wiltm'
  | 'music'
  | 'top8'
  | 'interests'
  | 'quote';

const sectionDetails: Record<ProfileEditorSection, {
  title: string;
  help: string;
  subject: string;
  saveLabel: string;
  focus: keyof EditorFields;
}> = {
  appearance: {
    title: 'Customise your SkySpace page',
    help: 'Change the classic background, colours, panels, type, cursor and trail effects.',
    subject: 'your page style',
    saveLabel: 'Save page style',
    focus: 'backgroundUrl',
  },
  about: {
    title: 'Edit About Me',
    help: 'This updates the About Me text on your Bluesky profile too.',
    subject: 'About Me',
    saveLabel: 'Save About Me',
    focus: 'description',
  },
  wiltm: {
    title: 'Who I’d Like To Meet',
    help: 'Who would you like to meet on SkySpace?',
    subject: 'Who I’d Like To Meet',
    saveLabel: 'Save',
    focus: 'wiltm',
  },
  music: {
    title: 'Edit Profile Music',
    help: 'Add a YouTube, Bandcamp, Apple Music, SoundCloud, Spotify or supported MIDI URL.',
    subject: 'profile music',
    saveLabel: 'Save Music',
    focus: 'music',
  },
  top8: {
    title: 'Pick Your Top 8',
    help: 'Add Bluesky handles beginning with @, separated by spaces or new lines.',
    subject: 'your Top 8',
    saveLabel: 'Save Top 8',
    focus: 'top8',
  },
  interests: {
    title: 'Edit Interests',
    help: 'Use [Category] followed by comma-separated interests, for example [General] Games, Music.',
    subject: 'your interests',
    saveLabel: 'Save Interests',
    focus: 'interests',
  },
  quote: {
    title: 'Edit Quote and Location',
    help: 'Your location remains searchable so nearby SkySpacers can find you.',
    subject: 'your quote',
    saveLabel: 'Save Quote',
    focus: 'quote',
  },
};

const appearanceDetails = {
  background: {
    title: 'Customise your SkySpace page',
    help: 'Choose an uploaded image, media URL, colour treatment or locally rendered shader.',
    subject: 'your page background',
    saveLabel: 'Save Background',
    focus: 'backgroundUrl' as keyof EditorFields,
  },
  frames: {
    title: 'Customise your SkySpace page',
    help: 'Choose the classic panel and frame for your page.',
    subject: 'your page frames',
    saveLabel: 'Save Frames',
    focus: 'panelTransparency' as keyof EditorFields,
  },
  font: {
    title: 'Customise your SkySpace page',
    help: 'Choose a suggested Google Font or enter any font family from fonts.google.com.',
    subject: 'your page font',
    saveLabel: 'Save Font',
    focus: 'font' as keyof EditorFields,
  },
  effects: {
    title: 'Customise your SkySpace page',
    help: 'Choose a cursor and an optional trail effect for your SkySpace.',
    subject: 'your page effects',
    saveLabel: 'Save Effects',
    focus: 'cursorImage' as keyof EditorFields,
  },
  layout: {
    title: 'Customise your SkySpace page',
    help: 'Arrange or hide the movable sections of your profile.',
    subject: 'your page layout',
    saveLabel: 'Save Layout',
    focus: 'backgroundUrl' as keyof EditorFields,
  },
};

const cursorSprites = [
  'auto',
  'default',
  'pointer',
  'crosshair',
  'move',
  'grab',
  'grabbing',
  'text',
  'custom',
];

export const suggestedGoogleFonts = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Source Sans 3',
  'Raleway',
  'Poppins',
  'Noto Sans',
  'Merriweather',
  'Playfair Display',
  'Ubuntu',
  'Nunito',
  'Rubik',
  'PT Sans',
  'Work Sans',
  'Fira Sans',
  'Inter',
  'Mulish',
  'Quicksand',
  'Barlow',
  'Manrope',
  'DM Sans',
  'Libre Franklin',
  'Karla',
  'Inconsolata',
  'Roboto Mono',
  'Space Mono',
  'IBM Plex Mono',
  'Bebas Neue',
  'Anton',
  'Lobster',
  'Pacifico',
  'Dancing Script',
  'Caveat',
  'Permanent Marker',
  'Abril Fatface',
  'Cinzel',
  'Cormorant Garamond',
  'Libre Baskerville',
  'Lora',
  'Crimson Text',
  'Josefin Sans',
  'Archivo',
  'Exo 2',
  'Orbitron',
  'Press Start 2P',
  'VT323',
  'Comic Neue',
  'Fredoka',
] as const;

const cursorTrails = [
  'none',
  'rainbow',
  'emoji',
  'elastic',
  'ghost',
  'trailing',
  'textflag',
  'dot',
  'bubbles',
  'snow',
  'fairy',
  'clock',
  'character',
];

export const cursorTrailModSuggestions: Record<string, string> = {
  rainbow: '20 #FE0000 #FD8C00 #FFE500 #119F0B #0644B3 #C22EDC 3',
  emoji: '🔥 🐬 🦆 16',
  elastic: '🤷‍♂️',
  ghost: 'https://example.com/ghost.png',
  trailing: 'https://example.com/cursor.png 15 0.8',
  textflag: '"Welcome to SkySpace!" #FF6800 12 monospace 14',
  dot: '#323232',
  fairy: '#D61C59 #E7D84B #1B8798',
  clock: 'blue black red black black',
  character: '"hello" "15px serif" #6622CC #A755C2 #B07C9E',
};

function safePreviewUrl(value: string): string {
  if (value.startsWith('blob:')) return value;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
  } catch {
    return '';
  }
}

function appearanceTheme(
  fields: EditorFields,
  data: SkyspaceProfileContent | undefined,
  backgroundUploadUrl: string,
  removeBackgroundImage: boolean,
): ClassicTheme {
  const records = asRecords(fields);
  const theme = normalizeClassicTheme(records.aesthetics);
  const backgroundUrl = safePreviewUrl(fields.backgroundSource === 'upload'
    ? backgroundUploadUrl
      || (!removeBackgroundImage ? data?.aesthetics?.value.bg?.resolvedUrl || '' : '')
    : fields.backgroundSource === 'url'
      ? fields.backgroundUrl
      : '');
  if (fields.backgroundSource === 'shader') return theme;
  if (!backgroundUrl) return theme;
  return {
    ...theme,
    backgroundUrl,
    backgroundKind: /\.(?:mp4|ogg|webm)(?:[?#]|$)/i.test(backgroundUrl)
      ? 'video'
      : shadertoyId(backgroundUrl)
        ? 'legacy-shadertoy'
        : 'image',
  };
}

export function ProfileEditor({
  profile,
  data,
  variant = 'header',
  label = 'Customise your page!',
  section = 'appearance',
  defaultOpen = false,
  onAppearancePreviewChange,
  onLayoutPreviewChange,
  subscriber = false,
}: {
  profile: ActorProfile;
  data?: SkyspaceProfileContent;
  variant?: 'header' | 'marker';
  label?: string;
  section?: ProfileEditorSection;
  defaultOpen?: boolean;
  subscriber?: boolean;
  onAppearancePreviewChange?(theme?: ClassicTheme): void;
  onLayoutPreviewChange?(preview?: {
    layout: ProfileLayout;
    customPanels: CustomPanel[];
  }): void;
}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [fields, setFields] = useState(() => editorFields(profile, data));
  const [appearancePane, setAppearancePane] = useState<
    'background' | 'frames' | 'font' | 'effects' | 'layout'
  >('background');
  const [customPanels, setCustomPanels] = useState(
    () => customPanelSlots(data?.content?.value.customPanels),
  );
  const [layout, setLayout] = useState(() => normalizeProfileLayout(
    data?.aesthetics?.value.layout,
    customPanelSlots(data?.content?.value.customPanels),
  ));
  const [backgroundFile, setBackgroundFile] = useState<File>();
  const [backgroundUploadUrl, setBackgroundUploadUrl] = useState('');
  const [removeBackgroundImage, setRemoveBackgroundImage] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const formRef = useRef<HTMLFormElement>(null);
  const backgroundFileInputRef = useRef<HTMLInputElement>(null);
  const shaderFileInputRef = useRef<HTMLInputElement>(null);
  const details = section === 'appearance'
    ? appearanceDetails[appearancePane]
    : sectionDetails[section];
  const liveAppearanceTheme = useMemo(
    () => appearanceTheme(
      fields,
      data,
      backgroundUploadUrl,
      removeBackgroundImage,
    ),
    [backgroundUploadUrl, data, fields, removeBackgroundImage],
  );
  const showsMultipleBackgroundColours = fields.backgroundColorMode !== 'solid'
    || Number(fields.backgroundColorCycleSpeed) > 0;
  useEffect(() => {
    if (section !== 'appearance') return;
    if (!open) {
      onAppearancePreviewChange?.(undefined);
      return;
    }
    if (fields.backgroundSource !== 'shader') {
      onAppearancePreviewChange?.(liveAppearanceTheme);
      return;
    }
    const timeout = window.setTimeout(() => {
      onAppearancePreviewChange?.(liveAppearanceTheme);
    }, 450);
    return () => window.clearTimeout(timeout);
  }, [
    fields.backgroundSource,
    liveAppearanceTheme,
    onAppearancePreviewChange,
    open,
    section,
  ]);
  useEffect(() => () => {
    if (section === 'appearance') onAppearancePreviewChange?.(undefined);
  }, [onAppearancePreviewChange, section]);
  useEffect(() => {
    if (section !== 'appearance' || !open || appearancePane !== 'layout') {
      onLayoutPreviewChange?.(undefined);
      return;
    }
    onLayoutPreviewChange?.({ layout, customPanels });
  }, [appearancePane, customPanels, layout, onLayoutPreviewChange, open, section]);
  useEffect(() => () => {
    if (section === 'appearance') onLayoutPreviewChange?.(undefined);
  }, [onLayoutPreviewChange, section]);
  useEffect(() => {
    if (!backgroundFile) {
      setBackgroundUploadUrl('');
      return;
    }
    const url = URL.createObjectURL(backgroundFile);
    setBackgroundUploadUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [backgroundFile]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pending) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, pending]);
  const focusField = details.focus;
  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      formRef.current
        ?.querySelector<HTMLElement>(`[data-editor-field="${focusField}"]`)
        ?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusField, open]);
  if (!auth.rpc || auth.did !== profile.did) return null;
  if (!open) {
    const openEditor = () => {
      setFields(editorFields(profile, data));
      setAppearancePane('background');
      const savedPanels = customPanelSlots(data?.content?.value.customPanels);
      setCustomPanels(savedPanels);
      setLayout(normalizeProfileLayout(data?.aesthetics?.value.layout, savedPanels));
      setBackgroundFile(undefined);
      setRemoveBackgroundImage(false);
      if (backgroundFileInputRef.current) backgroundFileInputRef.current.value = '';
      setOpen(true);
    };
    if (variant === 'marker') return <EditMarker label={label} onClick={openEditor} />;
    return (
      <button type="button" className="modern-customise-button" onClick={openEditor}>
        <EditGlyph large />
        <span>{label}</span>
      </button>
    );
  }
  return (
    <>
      {section === 'appearance' && variant === 'header' && (
        <div className="modern-customise-button modern-customising-indicator" aria-live="polite">
          <EditGlyph large />
          <span>Customising your page</span>
        </div>
      )}
      <div
        className={`overlay${section === 'appearance' ? ' modern-customisation-overlay' : ''}`}
        role="presentation"
        onMouseDown={event => {
        if (event.currentTarget === event.target && !pending) setOpen(false);
        }}
      >
      <form
        ref={formRef}
        className={`modal-content modern-profile-editor${section === 'appearance' ? ' modern-customisation-editor' : ' modern-subject-editor'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`modern-editor-title-${section}`}
        onSubmit={event => {
        event.preventDefault();
        setError(undefined);
        const records = asRecords(fields);
        if (section === 'appearance'
          && appearancePane === 'background'
          && fields.backgroundSource === 'shader') {
          const storedShader = records.aesthetics.bg?.shader;
          const problem = storedShader?.preset === 'custom'
            ? shaderSourceProblem(customShaderSource(storedShader))
            : shaderPreset(storedShader?.preset)
              ? undefined
              : 'Choose a shader before saving.';
          if (problem) {
            setError(problem);
            return;
          }
        }
        setPending(true);
        let save: Promise<void>;
        if (section === 'about') {
          save = saveBlueskyDescription(auth.rpc!, auth.did!, fields.description);
        } else if (section === 'quote') {
          save = saveSkyspaceAesthetics(
            auth.rpc!,
            auth.did!,
            {
              ...(data?.aesthetics?.value || {}),
              bio: records.aesthetics.bio,
            },
            data?.aesthetics?.uri,
          );
        } else if (section === 'appearance') {
          if (appearancePane === 'background') {
            save = (async () => {
              const uploadedImage = fields.backgroundSource === 'upload' && backgroundFile
                ? await uploadBackgroundImage(auth.rpc!, backgroundFile)
                : undefined;
              const shaderPreview = fields.backgroundSource === 'shader'
                ? await (async () => {
                  const storedShader = records.aesthetics.bg?.shader;
                  const imageCode = storedShader?.preset === 'custom'
                    ? customShaderSource(storedShader)
                    : (await import('../../lib/shader-presets'))
                      .shaderPresetSource(storedShader?.preset);
                  if (!imageCode) return undefined;
                  const { renderShaderStill } = await import('./shader-renderer');
                  return uploadBackgroundImage(
                    auth.rpc!,
                    await renderShaderStill(imageCode),
                  );
                })().catch(error => {
                  console.warn('SkySpace could not save a still preview of this shader.', error);
                  return undefined;
                })
                : undefined;
              const existingImage = data?.aesthetics?.value.bg?.image;
              const existingPreview = data?.aesthetics?.value.bg?.preview;
              const shaderUnchanged = JSON.stringify(data?.aesthetics?.value.bg?.shader || {})
                === JSON.stringify(records.aesthetics.bg?.shader || {});
              const preview = shaderPreview
                || (fields.backgroundSource === 'shader' && shaderUnchanged
                  ? existingPreview
                  : undefined);
              const image = fields.backgroundSource === 'upload'
                ? uploadedImage || (!removeBackgroundImage ? existingImage : undefined)
                : undefined;
              await saveSkyspaceAesthetics(
                auth.rpc!,
                auth.did!,
                {
                  ...(data?.aesthetics?.value || {}),
                  bg: {
                    ...records.aesthetics.bg,
                    url: fields.backgroundSource === 'url'
                      ? records.aesthetics.bg?.url
                      : '',
                    ...(image ? { image } : {}),
                    ...(preview ? { preview } : {}),
                  },
                },
                data?.aesthetics?.uri,
              );
            })();
          } else if (appearancePane === 'layout') {
            const aesthetics = { ...(data?.aesthetics?.value || {}), layout };
            save = !subscriber || JSON.stringify(customPanels) === JSON.stringify(
              customPanelSlots(data?.content?.value.customPanels),
            )
              ? saveSkyspaceAesthetics(auth.rpc!, auth.did!, aesthetics, data?.aesthetics?.uri)
              : saveSkyspaceProfile(
                auth.rpc!,
                auth.did!,
                { ...(data?.content?.value || {}), customPanels },
                aesthetics,
                {
                  contentUri: data?.content?.uri,
                  aestheticsUri: data?.aesthetics?.uri,
                },
              );
          } else {
            save = saveSkyspaceAesthetics(
              auth.rpc!,
              auth.did!,
              {
                ...(data?.aesthetics?.value || {}),
                ...(appearancePane === 'frames' ? {
                  panel: records.aesthetics.panel,
                } : appearancePane === 'font' ? {
                  text: records.aesthetics.text,
                } : {
                  effects: records.aesthetics.effects,
                }),
              },
              data?.aesthetics?.uri,
            );
          }
        } else {
          const field = section === 'wiltm' ? 'wiltm' : section;
          save = saveSkyspaceContent(
            auth.rpc!,
            auth.did!,
            {
              ...(data?.content?.value || {}),
              [field]: records.content[field],
            },
            data?.content?.uri,
          );
        }
        void save.then(async () => {
          await queryClient.invalidateQueries({
            queryKey: section === 'about'
              ? queryKeys.profile(profile.handle)
              : queryKeys.skyspaceContent(profile.did),
          });
          recordSkyspaceActivity(
            auth.did,
            auth.handle || profile.handle,
            section === 'appearance'
              ? 'appearance'
              : section === 'music'
                ? 'music'
                : section === 'interests'
                  ? 'interests'
                  : section === 'top8'
                    ? 'top8'
                    : 'profile',
          );
          setOpen(false);
        }).catch(reason => {
          setError(reason instanceof Error ? reason.message : `Could not save ${details.subject}.`);
        }).finally(() => setPending(false));
      }}
      >
        <button type="button" className="classic-close" onClick={() => setOpen(false)} aria-label="Close editor">×</button>
        <h2 id={`modern-editor-title-${section}`}>{details.title}</h2>
        {section !== 'appearance' && <p className="modern-editor-help">{details.help}</p>}
        {section === 'appearance' && (
          <p className="modern-live-customisation-note">
            Changes appear on your page as you make them. Save to keep them.
          </p>
        )}

        {section === 'appearance' && (
          <div className="modern-editor-tabs" role="tablist" aria-label="Page customisation">
            <button
              type="button"
              role="tab"
              aria-selected={appearancePane === 'background'}
              onClick={() => setAppearancePane('background')}
            >
              Background
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={appearancePane === 'frames'}
              onClick={() => setAppearancePane('frames')}
            >
              Frames
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={appearancePane === 'font'}
              onClick={() => setAppearancePane('font')}
            >
              Font
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={appearancePane === 'effects'}
              onClick={() => setAppearancePane('effects')}
            >
              Effects
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={appearancePane === 'layout'}
              onClick={() => setAppearancePane('layout')}
            >
              Layout
            </button>
          </div>
        )}

        {section === 'about' && (
          <fieldset>
            <legend>About Me</legend>
          <label><span>About Me</span><textarea data-editor-field="description" maxLength={256} value={fields.description} onChange={event => setFields({ ...fields, description: event.target.value })} /></label>
          </fieldset>
        )}

        {section === 'wiltm' && (
          <fieldset>
            <legend>Who I&apos;d Like To Meet</legend>
          <label><span>Who I&apos;d Like To Meet</span><textarea data-editor-field="wiltm" value={fields.wiltm} onChange={event => setFields({ ...fields, wiltm: event.target.value })} /></label>
          </fieldset>
        )}

        {section === 'music' && (
          <fieldset>
            <legend>Profile music</legend>
          <Input label="Profile music URL" field="music" fields={fields} setFields={setFields} type="url" />
          </fieldset>
        )}

        {section === 'top8' && (
          <fieldset>
            <legend>Top 8</legend>
          <label><span>Top 8 (@handles)</span><textarea data-editor-field="top8" value={fields.top8} onChange={event => setFields({ ...fields, top8: event.target.value })} /></label>
          </fieldset>
        )}

        {section === 'interests' && (
          <fieldset>
            <legend>Interests</legend>
          <label><span>Interests (for example [General] Games, Music)</span><textarea data-editor-field="interests" value={fields.interests} onChange={event => setFields({ ...fields, interests: event.target.value })} /></label>
          </fieldset>
        )}

        {section === 'quote' && (
          <fieldset>
            <legend>Quote and location</legend>
          <Input label="Quote" field="quote" fields={fields} setFields={setFields} />
          <Input label="Location" field="location" fields={fields} setFields={setFields} />
          </fieldset>
        )}

        {section === 'appearance' && appearancePane === 'background' && (
          <div className="modern-customisation-pane" role="tabpanel">
            <div className="modern-background-controls" aria-label="Background controls">
              <div
                className="modern-background-mode-buttons"
                role="tablist"
                aria-label="Background source"
              >
                {([
                  ['upload', 'Upload'],
                  ['url', 'URL'],
                  ['colour', 'Colour'],
                  ['shader', 'Shader'],
                ] as const).map(([value, text]) => (
                  <button
                    key={value}
                    type="button"
                    id={`modern-background-source-${value}`}
                    role="tab"
                    aria-selected={fields.backgroundSource === value}
                    aria-controls="modern-background-source-panel"
                    onClick={() => setFields({ ...fields, backgroundSource: value })}
                  >
                    {text}
                  </button>
                ))}
              </div>

              <input
                ref={backgroundFileInputRef}
                hidden
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={event => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setBackgroundFile(file);
                  setFields({ ...fields, backgroundSource: 'upload', backgroundUrl: '' });
                  setRemoveBackgroundImage(false);
                }}
              />

              <div
                className="modern-background-source-panel"
                id="modern-background-source-panel"
                role="tabpanel"
                aria-labelledby={`modern-background-source-${fields.backgroundSource}`}
              >
                {fields.backgroundSource === 'upload' && (
                  <div className="modern-background-upload-choice">
                    <div className="modern-classic-form-line">
                      <button
                        type="button"
                        className="modern-choose-upload"
                        onClick={() => backgroundFileInputRef.current?.click()}
                      >
                        Choose file
                      </button>
                      {(backgroundFile || (!removeBackgroundImage && data?.aesthetics?.value.bg?.image)) && (
                        <>
                          <span>{backgroundFile?.name || 'Uploaded image'}</span>
                          <button
                            type="button"
                            className="modern-remove-upload"
                            onClick={() => {
                              setBackgroundFile(undefined);
                              setRemoveBackgroundImage(true);
                              if (backgroundFileInputRef.current) {
                                backgroundFileInputRef.current.value = '';
                              }
                            }}
                          >
                            Remove uploaded image
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {fields.backgroundSource === 'url' && (
                  <div className="modern-background-url-choice">
                    <label className="modern-grow-control">
                      <span>URL</span>
                      <input
                        data-editor-field="backgroundUrl"
                        type="url"
                        value={fields.backgroundUrl}
                        placeholder="https://..."
                        onChange={event => setFields({
                          ...fields,
                          backgroundSource: 'url',
                          backgroundUrl: event.target.value,
                        })}
                      />
                    </label>
                    <p className="modern-background-advice">
                      Accepts image (.PNG / .GIF / .JPG) or movie (.MP4, .OGG, .WEBM) URLs.
                    </p>
                    {shadertoyId(fields.backgroundUrl) && (
                      <p className="modern-shader-status is-incompatible">
                        ShaderToy can no longer be imported automatically. Choose Shader above,
                        then paste its single-pass Image code.
                      </p>
                    )}
                  </div>
                )}

                {fields.backgroundSource === 'shader' && (
                  <div className="modern-background-shader-choice">
                    <label className="modern-grow-control">
                      <span>Shader</span>
                      <select
                        value={fields.shaderPreset}
                        onChange={event => setFields({
                          ...fields,
                          shaderPreset: event.target.value,
                        })}
                      >
                        {shaderPresets.map(preset => (
                          <option key={preset.id} value={preset.id}>{preset.name}</option>
                        ))}
                        <option value="custom">Custom GLSL…</option>
                      </select>
                    </label>

                    {fields.shaderPreset === 'custom' && (
                      <>
                        <input
                          ref={shaderFileInputRef}
                          hidden
                          type="file"
                          accept=".frag,.glsl,.txt,text/plain"
                          onChange={event => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            if (file.size > MAX_SHADER_SOURCE_LENGTH * 2) {
                              setError('That shader file is too large.');
                              return;
                            }
                            void file.text().then(code => {
                              setFields(current => ({ ...current, shaderCode: code }));
                              setError(undefined);
                            }).catch(() => setError('SkySpace could not read that shader file.'));
                          }}
                        />
                        <div className="modern-classic-form-line" style={{ marginTop: 8 }}>
                          <span>ShaderToy-compatible Image code</span>
                          <button
                            type="button"
                            className="modern-choose-upload"
                            onClick={() => shaderFileInputRef.current?.click()}
                          >
                            Upload .frag or .glsl
                          </button>
                        </div>
                        <textarea
                          className="modern-shader-code"
                          aria-label="Shader code"
                          maxLength={MAX_SHADER_SOURCE_LENGTH}
                          spellCheck={false}
                          value={fields.shaderCode}
                          placeholder="void mainImage(out vec4 colour, in vec2 coordinate) { ... }"
                          onChange={event => setFields({
                            ...fields,
                            shaderCode: event.target.value,
                          })}
                        />
                        {shaderSourceProblem(fields.shaderCode) && (
                          <p className="modern-shader-status is-incompatible">
                            {shaderSourceProblem(fields.shaderCode)}
                          </p>
                        )}
                      </>
                    )}

                    <div className="modern-shader-attribution modern-classic-form-line">
                      <label>
                        <span>Credit (optional)</span>
                        <input
                          maxLength={160}
                          value={fields.shaderAttribution}
                          placeholder="Shader title or creator"
                          onChange={event => setFields({
                            ...fields,
                            shaderAttribution: event.target.value,
                          })}
                        />
                      </label>
                      <label>
                        <span>Source link (optional)</span>
                        <input
                          type="url"
                          value={fields.shaderAttributionUrl}
                          placeholder="https://..."
                          onChange={event => setFields({
                            ...fields,
                            shaderAttributionUrl: event.target.value,
                          })}
                        />
                      </label>
                    </div>
                    <p className="modern-background-advice">
                      Custom code is stored in your AT Protocol profile record, not on SkySpace.
                      Single-pass Image shaders only; texture and buffer channels are not supported yet.
                    </p>
                  </div>
                )}

                {fields.backgroundSource === 'colour' && (
                  <div className="modern-background-colour-choice">
                    <div className="modern-classic-form-line modern-background-type-line">
                      <span>Type</span>
                      <div className="modern-background-colour-modes" aria-label="Colour background type">
                        {([
                          ['solid', 'Solid'],
                          ['pattern', 'Pattern'],
                          ['gradient', 'Gradient'],
                        ] as const).map(([value, text]) => (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={fields.backgroundColorMode === value}
                            onClick={() => setFields({
                              ...fields,
                              backgroundColorMode: value,
                              backgroundColors: value === 'solid'
                                ? fields.backgroundColors
                                : fields.backgroundColors.length > 1
                                  ? fields.backgroundColors
                                  : [fields.backgroundColor, '#669acc'],
                            })}
                          >
                            {text}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="modern-background-colours">
                      {(!showsMultipleBackgroundColours
                        ? fields.backgroundColors.slice(0, 1)
                        : fields.backgroundColors
                      ).map((colour, index) => (
                        <div className="modern-background-colour" key={index}>
                          <SimpleColourPicker
                            label={fields.backgroundColorMode === 'solid'
                              ? 'Colour'
                              : `Colour ${index + 1}`}
                            value={colour}
                            onChange={value => {
                              const colors = [...fields.backgroundColors];
                              colors[index] = value;
                              setFields({
                                ...fields,
                                backgroundColor: index === 0 ? value : fields.backgroundColor,
                                backgroundColors: colors,
                              });
                            }}
                          />
                          {showsMultipleBackgroundColours
                            && fields.backgroundColors.length > 2 && (
                            <button
                              type="button"
                              className="modern-remove-colour"
                              aria-label={`Remove colour ${index + 1}`}
                              onClick={() => setFields({
                                ...fields,
                                backgroundColors: fields.backgroundColors.filter((_, colourIndex) => (
                                  colourIndex !== index
                                )),
                              })}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      {showsMultipleBackgroundColours
                        && fields.backgroundColors.length < 6 && (
                        <button
                          type="button"
                          className="modern-add-colour"
                          onClick={() => setFields({
                            ...fields,
                            backgroundColors: [...fields.backgroundColors, '#ff5800'],
                          })}
                        >
                          + Add colour
                        </button>
                      )}
                    </div>

                    {fields.backgroundColorMode === 'pattern' && (
                      <label className="modern-background-pattern">
                        <span>Pattern</span>
                        <select
                          value={fields.backgroundPattern}
                          onChange={event => setFields({
                            ...fields,
                            backgroundPattern: event.target.value === 'checks' ? 'checks' : 'stripes',
                          })}
                        >
                          <option value="stripes">Stripes</option>
                          <option value="checks">Checks</option>
                        </select>
                      </label>
                    )}

                    {fields.backgroundColorMode === 'gradient' && (
                      <label className="modern-background-angle">
                        <span>Angle</span>
                        <input
                          type="range"
                          min="0"
                          max="360"
                          value={fields.backgroundGradientAngle}
                          onChange={event => setFields({
                            ...fields,
                            backgroundGradientAngle: event.target.value,
                          })}
                        />
                        <output>{fields.backgroundGradientAngle}°</output>
                      </label>
                    )}

                    <label className="modern-background-cycle-range">
                      <span>Cycling</span>
                      <input
                        type="range"
                        aria-label="Cycling speed"
                        min="0"
                        max="100"
                        step="5"
                        value={fields.backgroundColorCycleSpeed}
                        onChange={event => setFields({
                          ...fields,
                          backgroundColorCycleSpeed: event.target.value,
                          backgroundColors: Number(event.target.value) > 0
                            && fields.backgroundColors.length < 2
                            ? [fields.backgroundColor, '#669acc']
                            : fields.backgroundColors,
                        })}
                      />
                      <output>
                        {Number(fields.backgroundColorCycleSpeed) > 0
                          ? `${fields.backgroundColorCycleSpeed}%`
                          : 'Off'}
                      </output>
                    </label>
                  </div>
                )}

                {(fields.backgroundSource === 'upload' || fields.backgroundSource === 'url') && (
                  <label className="modern-background-position-line">
                    <span>Position</span>
                    <select value={fields.backgroundPosition} onChange={event => setFields({ ...fields, backgroundPosition: event.target.value })}>
                      {['default', 'tiled', 'centre', 'cover', 'contain', 'top left', 'top right', 'bottom left', 'bottom right'].map(value => (
                        <option key={value} value={value}>
                          {value.replace(/\b\w/g, character => character.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

        {section === 'appearance' && appearancePane === 'frames' && (
          <div className="modern-customisation-pane" role="tabpanel">
            <p className="modern-editor-help">{details.help}</p>
            <fieldset>
              <legend>Panel</legend>
              <div className="modern-classic-form-line">
                <label className="modern-range-control">
                  <span>Transparency</span>
                  <input data-editor-field="panelTransparency" type="range" min="0" max="1" step="0.05" value={fields.panelTransparency} onChange={event => setFields({ ...fields, panelTransparency: event.target.value })} />
                </label>
                <Input label="Colour" field="panelColor" fields={fields} setFields={setFields} type="color" />
                <Input label="Separator" field="separatorColor" fields={fields} setFields={setFields} type="color" />
              </div>
            </fieldset>
            <fieldset>
              <legend>Frame</legend>
              <div className="modern-classic-form-line">
                <Input label="Thickness" field="frameThickness" fields={fields} setFields={setFields} type="number" />
                <Input label="Radius" field="frameRadius" fields={fields} setFields={setFields} type="number" />
                <label><span>Style</span><select value={fields.frameStyle} onChange={event => setFields({ ...fields, frameStyle: event.target.value })}>
                  {['none', 'solid', 'double', 'dotted', 'dashed', 'groove', 'ridge', 'inset', 'outset'].map(value => <option key={value}>{value}</option>)}
                </select></label>
                <Input label="Colour" field="frameColor" fields={fields} setFields={setFields} type="color" />
              </div>
            </fieldset>
          </div>
        )}

        {section === 'appearance' && appearancePane === 'font' && (
          <div className="modern-customisation-pane" role="tabpanel">
            <p className="modern-editor-help">{details.help}</p>
            <div className="modern-font-controls" aria-label="Font controls">
              <div className="modern-classic-form-line modern-font-choice-line">
                <label>
                  <span>Suggested font</span>
                  <select
                    aria-label="Suggested font"
                    value={suggestedGoogleFonts.includes(fields.font as typeof suggestedGoogleFonts[number])
                      ? fields.font
                      : ''}
                    onChange={event => {
                      if (event.target.value) setFields({ ...fields, font: event.target.value });
                    }}
                  >
                    <option value="">Choose from 50 fonts…</option>
                    {suggestedGoogleFonts.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                </label>
                <span className="modern-font-or">or</span>
                <label className="modern-grow-control modern-font-name">
                  <span>Font name</span>
                  <input
                    data-editor-field="font"
                    value={fields.font}
                    placeholder="Name from fonts.google.com"
                    onChange={event => setFields({ ...fields, font: event.target.value })}
                  />
                </label>
              </div>
              <p className="modern-font-colour-label">Select your text colours.</p>
              <div className="modern-classic-form-line modern-font-colours">
                <Input label="Body" field="bodyColor" fields={fields} setFields={setFields} type="color" />
                <Input label="Title" field="titleColor" fields={fields} setFields={setFields} type="color" />
                <Input label="Link" field="linkColor" fields={fields} setFields={setFields} type="color" />
              </div>
            </div>
          </div>
        )}

        {section === 'appearance' && appearancePane === 'effects' && (
          <div className="modern-customisation-pane" role="tabpanel">
            <p className="modern-editor-help">{details.help}</p>
            <fieldset>
              <legend>Cursor</legend>
              <div className="modern-classic-form-line">
                <label>
                  <span>Sprite</span>
                  <select data-editor-field="cursorImage" value={fields.cursorImage} onChange={event => setFields({ ...fields, cursorImage: event.target.value })}>
                    {!cursorSprites.includes(fields.cursorImage) && <option value={fields.cursorImage}>{fields.cursorImage}</option>}
                    {cursorSprites.map(value => (
                      <option key={value} value={value}>
                        {value.replace(/\b\w/g, character => character.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="modern-grow-control">
                  <span className="sr-only">Custom Cursor URL</span>
                  <input type="url" value={fields.cursorUrl} placeholder="Custom Cursor URL" onChange={event => setFields({ ...fields, cursorUrl: event.target.value })} />
                </label>
              </div>
            </fieldset>
            <fieldset>
              <legend>Trail</legend>
              <div className="modern-classic-form-line">
                <label>
                  <span>Type</span>
                  <select value={fields.cursorTrail} onChange={event => setFields({
                    ...fields,
                    cursorTrail: event.target.value,
                    cursorTrailParameters: '',
                  })}>
                    {cursorTrails.map(value => (
                      <option key={value} value={value}>
                        {value.replace(/\b\w/g, character => character.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="modern-grow-control">
                  <span>Mods</span>
                  <input
                    value={trailSupportsMods(fields.cursorTrail)
                      ? fields.cursorTrailParameters
                      : ''}
                    size={48}
                    disabled={!trailSupportsMods(fields.cursorTrail)}
                    aria-label={`Mods for ${fields.cursorTrail} trail`}
                    placeholder={cursorTrailModSuggestions[fields.cursorTrail] || 'No modifications for this trail'}
                    onChange={event => setFields({
                      ...fields,
                      cursorTrailParameters: event.target.value,
                    })}
                  />
                </label>
              </div>
            </fieldset>
          </div>
        )}

        {section === 'appearance' && appearancePane === 'layout' && (
          <div className="modern-customisation-pane" role="tabpanel">
            <Suspense fallback={<p className="modern-editor-help">Opening layout controls…</p>}>
              <LayoutEditor
                layout={layout}
                customPanels={customPanels}
                subscriber={subscriber}
                onChange={setLayout}
                onPanelsChange={setCustomPanels}
              />
            </Suspense>
          </div>
        )}

        {error && <p role="alert">{error}</p>}
        <div className="modern-editor-actions">
          <button type="submit" disabled={pending}>{pending ? 'Saving…' : details.saveLabel}</button>
        </div>
      </form>
      </div>
    </>
  );
}
